// frontend/src/components/jobPlacement/JobSearchContactForm.jsx
import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { getApiBaseURL } from "../../utils/api";
import secureAxios from "../../utils/secureAxios";
import { toast } from "react-toastify";

const today = new Date().toISOString().split("T")[0];

const JobSearchContactForm = ({
  contact,
  consumer,
  consumers = [], // List of consumers for selection when consumer is null
  jobPlacement,
  onSave,
  onCancel,
}) => {
  const { user } = useContext(AuthContext);
  const apiBase = getApiBaseURL();

  const [selectedConsumer, setSelectedConsumer] = useState(consumer || null);
  const [formData, setFormData] = useState({
    contact_date: today,
    employer_id: "",
    employer_name: "",
    result_of_contact: "",
    comments: "",
  });
  const [otherResult, setOtherResult] = useState(""); // Separate state for "Other" text
  const [showOtherInput, setShowOtherInput] = useState(false);

  const [employers, setEmployers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [useManualEntry, setUseManualEntry] = useState(false);
  const [addToEmployersList, setAddToEmployersList] = useState(false);

  // Update selectedConsumer when consumer prop changes
  useEffect(() => {
    if (consumer) {
      setSelectedConsumer(consumer);
    }
  }, [consumer]);

  // Load employers dropdown
  useEffect(() => {
    const loadEmployers = async () => {
      if (!user?.tenant_id) return;

      try {
        const res = await secureAxios.get(`${apiBase}/api/employers/dropdown`, {
          params: { tenant_id: user.tenant_id },
        });
        setEmployers(res.data);
      } catch (err) {
        console.error("Error loading employers:", err);
      }
    };
    loadEmployers();
  }, [user?.tenant_id, apiBase]);

  // Populate form when editing
  useEffect(() => {
    if (contact) {
      // Check if the result_of_contact is a custom "Other" value
      const isOtherValue =
        contact.result_of_contact &&
        !resultOptions.includes(contact.result_of_contact) &&
        contact.result_of_contact !== "";

      setFormData({
        contact_date: contact.contact_date?.split("T")[0] || today,
        employer_id: contact.employer_id || "",
        employer_name: contact.employer_name || "",
        result_of_contact: isOtherValue
          ? "Other"
          : contact.result_of_contact || "",
        comments: contact.comments || "",
      });

      if (isOtherValue) {
        setShowOtherInput(true);
        setOtherResult(contact.result_of_contact);
      }

      // If there's an employer name but no employer_id, use manual entry
      if (contact.employer_name && !contact.employer_id) {
        setUseManualEntry(true);
      }
    }
  }, [contact]);

  // Common result options - defined before useEffect that uses it
  const resultOptions = [
    "Left message",
    "Spoke with contact - No openings",
    "Spoke with contact - Will call back",
    "Submitted application",
    "Scheduled interview",
    "Interview completed",
    "Job offer received",
    "Position filled",
    "Not hiring at this time",
    "Referred to another location",
    "Other",
  ];

  const handleConsumerChange = (e) => {
    const consumerId = e.target.value;
    const found = consumers.find((c) => c.consumer_id === consumerId);
    setSelectedConsumer(found || null);
    if (error) setError("");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "employer_id" && value) {
      // When selecting from dropdown, auto-fill employer name
      const selectedEmployer = employers.find(
        (emp) => emp.employer_id === value
      );
      if (selectedEmployer) {
        setFormData((prev) => ({
          ...prev,
          employer_id: value,
          employer_name: selectedEmployer.company_name,
        }));
        return;
      }
    }

    // Handle result_of_contact "Other" selection
    if (name === "result_of_contact") {
      if (value === "Other") {
        setShowOtherInput(true);
        setOtherResult("");
      } else {
        setShowOtherInput(false);
        setOtherResult("");
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (error) setError("");
  };

  const handleOtherResultChange = (e) => {
    setOtherResult(e.target.value);
  };

  const handleToggleManualEntry = () => {
    setUseManualEntry(!useManualEntry);
    setAddToEmployersList(false); // Reset when toggling
    if (!useManualEntry) {
      // Switching to manual entry - clear employer_id
      setFormData((prev) => ({
        ...prev,
        employer_id: "",
      }));
    } else {
      // Switching to dropdown - clear manual name
      setFormData((prev) => ({
        ...prev,
        employer_name: "",
      }));
    }
  };

  const validateForm = () => {
    // Consumer is optional - no validation needed

    if (!formData.contact_date) {
      setError("Contact date is required");
      return false;
    }

    if (!formData.employer_name.trim()) {
      setError("Employer name is required");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setError("");

    // Determine the actual result_of_contact value
    const actualResultOfContact =
      showOtherInput && otherResult.trim()
        ? otherResult.trim()
        : formData.result_of_contact;

    let employerId = formData.employer_id || null;

    try {
      // If using manual entry and checkbox is checked, create employer first
      if (
        useManualEntry &&
        addToEmployersList &&
        formData.employer_name.trim()
      ) {
        try {
          const employerRes = await secureAxios.post(
            `${apiBase}/api/employers`,
            {
              tenant_id: user.tenant_id,
              company_name: formData.employer_name.trim(),
              created_by: user.user_id,
            }
          );

          if (employerRes.data?.employer_id) {
            employerId = employerRes.data.employer_id;
            toast.success(
              `Employer "${formData.employer_name}" added to employers list`
            );
          }
        } catch (empErr) {
          console.error("Error creating employer:", empErr);
          // Don't fail the whole operation, just warn
          toast.warning(
            "Could not add employer to list, but contact will still be saved"
          );
        }
      }

      const payload = {
        ...formData,
        employer_id: employerId,
        result_of_contact: actualResultOfContact,
        consumer_id: selectedConsumer?.consumer_id || null,
        job_placement_id: jobPlacement?.job_placement_id || null,
        tenant_id: user.tenant_id,
        [contact ? "updated_by" : "created_by"]: user.user_id,
      };

      if (contact) {
        await secureAxios.put(
          `${apiBase}/api/job-search-contacts/${contact.contact_id}`,
          payload
        );
        toast.success("Contact updated successfully");
      } else {
        await secureAxios.post(`${apiBase}/api/job-search-contacts`, payload);
        toast.success("Contact logged successfully");
      }

      onSave();
    } catch (err) {
      console.error("Error saving contact:", err);
      setError(err.response?.data?.error || "Failed to save contact");
    } finally {
      setLoading(false);
    }
  };

  return (
    
   <div className="container my-4">
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-2 mb-md-3 gap-2">
        <h5>
          <i className="bi bi-telephone me-2"></i>
          {contact ? "Edit Job Search Contact" : "Log Job Search Contact"}
        </h5>
        <button className="btn btn-outline-secondary btn-sm" onClick={onCancel}>
          <i className="bi bi-arrow-left me-1"></i> Back
        </button>
      </div>

      {/* Consumer Selection - show dropdown if no consumer pre-selected */}
      {!consumer && consumers.length > 0 && (
        <div className="card mb-2 mb-md-3">
          <div className="card-body p-2 p-md-3">
            <label className="form-label">
              <strong>Consumer</strong>{" "}
              <span className="text-muted">(optional)</span>
            </label>
            <select
              className="form-select form-select-sm"
              value={selectedConsumer?.consumer_id || ""}
              onChange={handleConsumerChange}
              disabled={loading || !!contact}
            >
              <option value="">Select a consumer...</option>
              {consumers.map((c) => (
                <option key={c.consumer_id} value={c.consumer_id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Consumer Info - show when consumer is selected */}
      {selectedConsumer && (
        <div className="alert alert-info py-2 mb-3">
          <strong>Consumer:</strong> {selectedConsumer.name}
          {jobPlacement && (
            <>
              {" | "}
              <strong>Goal:</strong> {jobPlacement.vocational_goal || "-"}
            </>
          )}
        </div>
      )}

      {error && (
        <div className="alert alert-danger alert-dismissible">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError("")}
          ></button>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="row g-2 g-md-3">
          {/* Contact Date */}
          <div className="col-md-3">
            <label className="form-label">
              Contact Date <span className="text-danger">*</span>
            </label>
            <input
              type="date"
              name="contact_date"
              className="form-control form-control-sm"
              value={formData.contact_date}
              onChange={handleChange}
              required
              disabled={loading}
            />
          </div>

          {/* Employer Selection */}
          <div className="col-md-6">
            <label className="form-label">
              Employer <span className="text-danger">*</span>
              <button
                type="button"
                className="btn btn-link btn-sm p-0 ms-2"
                onClick={handleToggleManualEntry}
              >
                {useManualEntry ? "Select from list" : "Enter manually"}
              </button>
            </label>

            {useManualEntry ? (
              <>
                <input
                  type="text"
                  name="employer_name"
                  className="form-control form-control-sm"
                  value={formData.employer_name}
                  onChange={handleChange}
                  placeholder="Enter employer name"
                  required
                  disabled={loading}
                />
                <div className="form-check mt-2">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="addToEmployersList"
                    checked={addToEmployersList}
                    onChange={(e) => setAddToEmployersList(e.target.checked)}
                    disabled={loading}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="addToEmployersList"
                  >
                    <small>Also add to Employers list</small>
                  </label>
                </div>
              </>
            ) : (
              <select
                name="employer_id"
                className="form-select form-select-sm"
                value={formData.employer_id}
                onChange={handleChange}
                disabled={loading}
              >
                <option value="">Select an employer...</option>
                {employers.map((emp) => (
                  <option key={emp.employer_id} value={emp.employer_id}>
                    {emp.company_name}
                    {emp.contact_name ? ` (${emp.contact_name})` : ""}
                  </option>
                ))}
              </select>
            )}

            {!useManualEntry && employers.length === 0 && (
              <small className="text-muted">
                No employers found.{" "}
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0"
                  onClick={handleToggleManualEntry}
                >
                  Enter manually
                </button>
              </small>
            )}
          </div>

          {/* Quick Actions */}
          <div className="col-md-3 d-flex align-items-end">
            {!useManualEntry && formData.employer_id && (
              <a
                href={`/crm/employers`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-outline-secondary btn-sm"
              >
                <i className="bi bi-box-arrow-up-right me-1"></i>
                View Employer
              </a>
            )}
          </div>

          {/* Result of Contact */}
          <div className="col-md-6">
            <label className="form-label">Result of Contact</label>
            <select
              name="result_of_contact"
              className="form-select form-select-sm"
              value={showOtherInput ? "Other" : formData.result_of_contact}
              onChange={handleChange}
              disabled={loading}
            >
              <option value="">Select result...</option>
              {resultOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
            {showOtherInput && (
              <input
                type="text"
                className="form-control mt-2"
                placeholder="Specify other result..."
                value={otherResult}
                onChange={handleOtherResultChange}
                disabled={loading}
              />
            )}
          </div>

          {/* Comments */}
          <div className="col-md-6">
            <label className="form-label">Comments</label>
            <textarea
              name="comments"
              className="form-control form-control-sm"
              value={formData.comments}
              onChange={handleChange}
              rows="2"
              placeholder="Additional notes..."
              disabled={loading}
            />
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="mt-4">
          <button
            type="submit"
            className="btn btn-primary me-2"
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Saving...
              </>
            ) : (
              <>
                <i className="bi bi-check-circle me-2"></i>
                {contact ? "Update Contact" : "Log Contact"}
              </>
            )}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
   
  );
};

export default JobSearchContactForm;